import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { ChevronRight, ChevronLeft, X, MousePointerClick, Zap, BookOpen } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';

const COLORS = { cube: '#18181B', sphere: '#2563EB', pyramid: '#10B981' };

const ABOUT_TEXT = `Open Structures is not just an interactive experience — it is a meditation on becoming, a digital embodiment of how knowledge, uncertainty, and imagination co‑evolve.

In the world of Open Structures, every placed piece is more than a shape — it is a hypothesis, an assertion against the vast field of the Unknown. Each form you draw and each structure you complete is a momentary crystallization of understanding within a space that refuses final definition.

This space, the Unknown, is not chaos nor emptiness. It is the permission field, the ontological ground of possibility itself — the underlying condition that makes emergence, creativity, and even laws of order possible. No amount of measurement, pattern, or certainty ever fully contains it, because the act of knowing itself expands it. As knowledge grows, so too does what remains unknown, a horizon that forever shifts just beyond our grasp.

In Open Structures, players move on the boundary between these domains:

— The Known: the shapes you place, the patterns you discover
— The Not‑Yet‑Known: the anticipations and emergent confidences
— The Unknown: the fertile field of potential that resists capture yet sustains all possibility

Every act of building is not an attempt to conquer this field, but a moment of participation with it. Structures harden into equilibrium, local islands of certainty, but when they become too rigid, they begin to break down. Certainty that refuses to yield loses contact with the world of potential and is dissolved by the very Unknown it sought to contain.

Thus the cycle of Open Structures becomes a metaphor for the evolution of understanding itself:

— Creation: placing new pieces and forming hypotheses
— Equilibrium: reaching a coherent structure, a local "known"
— Disintegration: when certainty becomes too rigid, it collapses
— Re‑emergence: the cycle renews and the Unknown beckons

This is not merely a game mechanic, but a philosophical movement between state and flux, between certainty and openness.

Like human theories throughout history, each explanatory structure stands until something unfolds that lies outside its frame, demanding reconfiguration. Classical models give way to new paradigms; certainty becomes provisional; what we once took for truth becomes the seedbed of new mysteries. The journey through Open Structures mirrors this evolution of knowledge itself: a continual oscillation between what we define and what forever eludes final definition — the eternal field of becoming.`;

function tSvg(type, cx, cy, size, colorOverride) {
  const half = size / 2 - 2;
  const color = colorOverride || COLORS[type] || '#18181B';
  switch (type) {
    case 'cube': return `<rect x="${cx - half}" y="${cy - half}" width="${half * 2}" height="${half * 2}" fill="${color}" rx="2"/>`;
    case 'sphere': return `<circle cx="${cx}" cy="${cy}" r="${half}" fill="${color}"/>`;
    case 'pyramid': return `<polygon points="${cx},${cy - half} ${cx - half},${cy + half} ${cx + half},${cy + half}" fill="${color}"/>`;
    default: return '';
  }
}

// Tutorial steps - comprehensive mechanics explanation
const STEPS = [
  { 
    title: 'Select & Place', 
    description: 'Choose a shape from the left. Click anywhere on the field to place it. A shadow follows your cursor.', 
    visual: 'place' 
  },
  { 
    title: 'Confidence Rises', 
    description: 'Each shape adds confidence. Patterns make it rise faster. Green zone (60-85%) is stable. At 100%, destruction follows.', 
    visual: 'meter' 
  },
  { 
    title: 'Equilibrium & Rewards', 
    description: 'Maintain confidence in the green zone (60-85%) for a sustained period to reach equilibrium. Base time is 75 seconds, but difficulty modifies this. You\'ll win special shapes that persist across rounds!', 
    visual: 'equilibrium' 
  },
  { 
    title: 'Unlock New Tools', 
    description: 'New shapes and tools unlock as you reach equilibrium: Hexagon (1), Diamond (3), Rotate tool (5), Pen tool (7), Star (10). Keep playing to unlock them all!', 
    visual: 'unlocks' 
  },
  { 
    title: 'Force Unknown', 
    description: 'Press the orange button to introduce chaos. It lowers confidence and can cause radical effects at higher difficulty. Use strategically.', 
    visual: 'chaos' 
  },
  { 
    title: 'The Game Learns', 
    description: 'Each session, the game learns your patterns. Difficulty increases with each equilibrium reached. Your preferred shapes, building style, and history shape future challenges.', 
    visual: 'learning' 
  },
];

function StepVisual({ type }) {
  switch (type) {
    case 'place':
      return (
        <div className="flex flex-col items-center gap-3 mx-auto">
          <div className="flex gap-3">
            {['cube', 'sphere', 'pyramid'].map((t, i) => (
              <div key={t} className={`p-2 border rounded-sm ${i === 0 ? 'border-zinc-900 bg-zinc-900' : 'border-zinc-200'}`}>
                <svg width={28} height={28} viewBox="0 0 28 28"
                  dangerouslySetInnerHTML={{ __html: tSvg(t, 14, 14, 28, i === 0 ? '#fff' : undefined) }}
                />
              </div>
            ))}
          </div>
          <span className="text-xs font-mono text-zinc-400 flex items-center gap-1">
            <MousePointerClick size={12} /> select, then click field
          </span>
        </div>
      );
    case 'meter':
      return (
        <div className="flex items-end gap-6 mx-auto justify-center h-20">
          <div className="flex flex-col items-center gap-1">
            <div className="w-8 bg-zinc-100 rounded-sm overflow-hidden relative" style={{ height: '60px' }}>
              <div className="absolute bottom-0 w-full rounded-sm bg-blue-500" style={{ height: '30%' }} />
            </div>
            <span className="text-xs font-mono text-zinc-400">low</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="w-8 bg-zinc-100 rounded-sm overflow-hidden relative" style={{ height: '60px' }}>
              <div className="absolute bottom-0 w-full rounded-sm bg-emerald-500" style={{ height: '70%' }} />
            </div>
            <span className="text-xs font-mono text-emerald-600">stable</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="w-8 bg-zinc-100 rounded-sm overflow-hidden relative" style={{ height: '60px' }}>
              <div className="absolute bottom-0 w-full rounded-sm bg-red-500" style={{ height: '100%' }} />
            </div>
            <span className="text-xs font-mono text-red-500">100%</span>
          </div>
        </div>
      );
    case 'chaos':
      return (
        <div className="flex flex-col items-center gap-3 mx-auto">
          <div className="bg-orange-600 text-white rounded-sm px-4 py-2 font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <Zap size={16} /> Force Unknown
          </div>
          <span className="text-xs font-mono text-zinc-500">lowers confidence, causes chaos effects</span>
        </div>
      );
    case 'pen':
      return (
        <div className="flex flex-col items-center gap-4 mx-auto">
          <div className="flex items-center gap-6">
            <div className="flex flex-col items-center gap-2">
              <div className="p-3 border border-zinc-300 rounded-sm bg-zinc-50">
                <svg width={32} height={32} viewBox="0 0 32 32">
                  <line x1="6" y1="16" x2="26" y2="16" stroke="#18181B" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </div>
              <span className="text-xs text-zinc-500">Draw line</span>
            </div>
            <div className="text-zinc-300 text-lg">|</div>
            <div className="flex flex-col items-center gap-2">
              <div className="p-3 border border-zinc-300 rounded-sm bg-zinc-50 relative">
                <svg width={32} height={32} viewBox="0 0 32 32">
                  <rect x="8" y="8" width="16" height="16" fill="#2563EB" rx="2"/>
                </svg>
                <div className="absolute -top-1 -right-1 bg-zinc-900 text-white text-[8px] px-1 rounded">45°</div>
              </div>
              <span className="text-xs text-zinc-500">Click to rotate</span>
            </div>
          </div>
          <span className="text-xs font-mono text-zinc-400">straight lines = more confidence</span>
        </div>
      );
    case 'equilibrium':
      return (
        <div className="flex flex-col items-center gap-3 mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-8 bg-zinc-100 rounded-sm overflow-hidden relative" style={{ height: '50px' }}>
              <div className="absolute bottom-0 w-full rounded-sm bg-emerald-500" style={{ height: '75%' }} />
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-emerald-600">75s</div>
              <div className="text-xs text-zinc-400">in green zone</div>
            </div>
            <div className="text-2xl">→</div>
            <div className="bg-purple-100 border border-purple-300 rounded-sm px-3 py-2">
              <span className="text-xl">✚</span>
              <div className="text-xs text-purple-600">Special Shape!</div>
            </div>
          </div>
        </div>
      );
    case 'learning':
      return (
        <div className="flex flex-col items-center gap-3 mx-auto">
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="bg-zinc-100 rounded-sm p-2">
              <div className="font-bold text-zinc-700">Difficulty</div>
              <div className="text-zinc-500">↑ per equilibrium</div>
            </div>
            <div className="bg-zinc-100 rounded-sm p-2">
              <div className="font-bold text-zinc-700">Patterns</div>
              <div className="text-zinc-500">tracked</div>
            </div>
            <div className="bg-zinc-100 rounded-sm p-2">
              <div className="font-bold text-zinc-700">History</div>
              <div className="text-zinc-500">remembered</div>
            </div>
          </div>
          <span className="text-xs font-mono text-zinc-500">the game adapts to how you play</span>
        </div>
      );
    case 'unlocks':
      return (
        <div className="flex flex-col items-center gap-3 mx-auto">
          <div className="flex items-center gap-1.5">
            <div className="flex flex-col items-center">
              <div className="p-1.5 border border-emerald-300 bg-emerald-50 rounded-sm">
                <svg width={16} height={16} viewBox="0 0 20 20">
                  <polygon points="10,2 18,7 18,15 10,18 2,15 2,7" fill="#10B981"/>
                </svg>
              </div>
              <span className="text-[9px] text-zinc-500 mt-0.5">1</span>
            </div>
            <div className="text-zinc-300 text-xs">→</div>
            <div className="flex flex-col items-center">
              <div className="p-1.5 border border-amber-300 bg-amber-50 rounded-sm">
                <svg width={16} height={16} viewBox="0 0 20 20">
                  <polygon points="10,2 15,10 10,18 5,10" fill="#F59E0B"/>
                </svg>
              </div>
              <span className="text-[9px] text-zinc-500 mt-0.5">3</span>
            </div>
            <div className="text-zinc-300 text-xs">→</div>
            <div className="flex flex-col items-center">
              <div className="p-1.5 border border-sky-300 bg-sky-50 rounded-sm">
                <svg width={16} height={16} viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="6" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeDasharray="25 10"/>
                  <polygon points="14,4 18,6 15,9" fill="#0EA5E9"/>
                </svg>
              </div>
              <span className="text-[9px] text-zinc-500 mt-0.5">5</span>
            </div>
            <div className="text-zinc-300 text-xs">→</div>
            <div className="flex flex-col items-center">
              <div className="p-1.5 border border-zinc-300 bg-zinc-50 rounded-sm">
                <svg width={16} height={16} viewBox="0 0 20 20">
                  <line x1="4" y1="10" x2="16" y2="10" stroke="#374151" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </div>
              <span className="text-[9px] text-zinc-500 mt-0.5">7</span>
            </div>
            <div className="text-zinc-300 text-xs">→</div>
            <div className="flex flex-col items-center">
              <div className="p-1.5 border border-pink-300 bg-pink-50 rounded-sm">
                <svg width={16} height={16} viewBox="0 0 20 20">
                  <polygon points="10,2 12,8 18,8 13,12 15,18 10,14 5,18 7,12 2,8 8,8" fill="#EC4899"/>
                </svg>
              </div>
              <span className="text-[9px] text-zinc-500 mt-0.5">10</span>
            </div>
          </div>
          <span className="text-xs font-mono text-zinc-400">reach equilibrium to unlock new tools</span>
        </div>
      );
    default:
      return null;
  }
}

export const TutorialOverlay = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [showAbout, setShowAbout] = useState(false);

  const handleClose = () => { setStep(0); onClose(); };

  if (!open) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        data-testid="tutorial-overlay"
      >
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white border border-zinc-200 rounded-sm max-w-md w-full relative"
        >
          <button onClick={handleClose} className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-900 transition-colors" data-testid="tutorial-close-btn">
            <X size={18} />
          </button>

          <div className="flex gap-1 px-6 pt-5">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-zinc-900' : 'bg-zinc-200'}`} />
            ))}
          </div>

          <div className="px-6 pt-5 pb-4 space-y-5">
            <div>
              <h3 className="text-lg font-bold tracking-tight">{current.title}</h3>
              <p className="text-sm text-zinc-500 leading-relaxed mt-2">{current.description}</p>
            </div>
            <div className="bg-zinc-50 border border-zinc-100 rounded-sm p-6 min-h-[90px] flex items-center justify-center">
              <StepVisual type={current.visual} />
            </div>
          </div>

          <div className="flex items-center justify-between px-6 pb-5">
            <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} disabled={isFirst} className="gap-1 text-xs" data-testid="tutorial-prev-btn">
              <ChevronLeft size={14} /> Back
            </Button>
            <span className="text-xs font-mono text-zinc-400">{step + 1} / {STEPS.length}</span>
            {isLast ? (
              <Button size="sm" onClick={handleClose} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm gap-1 text-xs px-4" data-testid="tutorial-start-btn">
                Begin
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStep(s => s + 1)} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm gap-1 text-xs px-4" data-testid="tutorial-next-btn">
                Next <ChevronRight size={14} />
              </Button>
            )}
          </div>
          
          {/* About Link */}
          <div className="border-t border-zinc-100 px-6 py-3">
            <button
              onClick={() => setShowAbout(true)}
              className="w-full flex items-center justify-center gap-2 text-zinc-400 hover:text-zinc-600 transition-colors text-xs font-mono"
              data-testid="tutorial-about-btn"
            >
              <BookOpen size={12} />
              About Open Structures
            </button>
          </div>
        </motion.div>
        
        {/* About Dialog */}
        <Dialog open={showAbout} onOpenChange={setShowAbout}>
          <DialogContent className="max-w-2xl max-h-[80vh] rounded-sm">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Open Structures</DialogTitle>
              <DialogDescription className="text-zinc-500">
                A meditation on becoming
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[60vh] pr-4">
              <div className="prose prose-zinc prose-sm max-w-none">
                {ABOUT_TEXT.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="text-zinc-600 leading-relaxed mb-4 whitespace-pre-wrap">
                    {paragraph}
                  </p>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </motion.div>
    </AnimatePresence>
  );
};
